/**
 * Task Management Page
 * Wrapper component that handles profile selection and GMB data loading
 * 
 * @author Visibeen Team - 2024
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Paper,
    CircularProgress,
    Alert,
    Button,
    Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import BusinessIcon from '@mui/icons-material/Business';

import TaskManagement from './TaskManagement';
import GMBService from '../../services/GMBService';
import CompetitorAnalysisService from '../../services/CompetitorAnalysisService';
import { getCompetitorInsights } from '../../utils/yourDataStore';
import logger from '../../utils/logger';
import { API_BASE_URL } from '../../config/api';

// ============================================
// Styled Components
// ============================================

const PageContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    padding: '16px',
    [theme.breakpoints.up('sm')]: {
        padding: '20px'
    },
    [theme.breakpoints.up('md')]: {
        padding: '24px'
    }
}));

const ProfileSelectorCard = styled(Paper)(({ theme }) => ({
    padding: '16px',
    marginBottom: '16px',
    borderRadius: '12px',
    background: 'white',
    border: '1px solid #e2e8f0',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '12px',
    maxWidth: '900px',
    margin: '0 auto 16px auto',
    [theme.breakpoints.up('sm')]: {
        padding: '20px 24px',
        marginBottom: '20px',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
        borderRadius: '16px'
    },
    [theme.breakpoints.up('md')]: {
        marginBottom: '24px'
    }
}));

const ProfileSelect = styled(FormControl)(({ theme }) => ({
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#f8fafc',
        '&:hover': {
            backgroundColor: '#f1f5f9'
        },
        '&.Mui-focused': {
            backgroundColor: 'white'
        }
    },
    [theme.breakpoints.up('sm')]: {
        minWidth: '300px',
        width: 'auto',
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px'
        }
    }
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    textAlign: 'center',
    padding: '24px',
    [theme.breakpoints.up('sm')]: {
        minHeight: '400px'
    }
}));

// ============================================
// Main Component
// ============================================

function TaskManagementPage() {
    const [searchParams] = useSearchParams();

    // State
    const [profiles, setProfiles] = useState([]);
    const [selectedProfileId, setSelectedProfileId] = useState('');
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [gmbData, setGmbData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(null);

    // ============================================
    // Data Loading Functions
    // ============================================

    const fetchProfiles = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            logger.log('[TaskManagementPage] Fetching GMB profiles...');

            const accounts = await GMBService.getAccounts();

            if (!accounts || accounts.length === 0) {
                setError('No GMB accounts found. Please connect your Google Business Profile.');
                setLoading(false);
                return;
            }

            const allProfiles = [];

            for (const account of accounts) {
                try {
                    const locations = await GMBService.getLocations(undefined, account.name);
                    if (locations && locations.length > 0) {
                        locations.forEach(location => {
                            allProfiles.push({
                                id: location.name,
                                accountId: account.name?.split('/')[1] || account.name,
                                locationId: location.name?.split('/')[1] || location.name,
                                name: location.name,
                                title: location.title || location.locationName || 'Unnamed Business',
                                address: location.storefrontAddress?.addressLines?.join(', ') || '',
                                verified: location.metadata?.verified || false,
                                ...location
                            });
                        });
                    }
                } catch (locErr) {
                    logger.warn('[TaskManagementPage] Error fetching locations for account:', account.name, locErr);
                }
            }

            logger.log('[TaskManagementPage] Loaded profiles:', allProfiles.length);
            setProfiles(allProfiles);

            // Auto-select first profile or from URL
            const urlProfileId = searchParams.get('profileId');
            if (urlProfileId && allProfiles.find(p => p.id === urlProfileId || p.locationId === urlProfileId)) {
                setSelectedProfileId(urlProfileId);
            } else if (allProfiles.length > 0) {
                setSelectedProfileId(allProfiles[0].id);
            }

        } catch (err) {
            logger.error('[TaskManagementPage] Error fetching profiles:', err);
            setError('Failed to load profiles. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [searchParams]);

    const fetchGMBData = useCallback(async (profile) => {
        if (!profile) return;

        try {
            setDataLoading(true);
            setError(null);

            logger.log('[TaskManagementPage] Fetching GMB data for:', profile.title);

            // Compile comprehensive GMB data for task analysis
            const gmbDataPayload = {
                ...profile,
                profile: profile,
                metadata: profile.metadata || {},
                phoneNumbers: profile.phoneNumbers || {},
                phoneNumber: profile.phoneNumbers?.primaryPhone || profile.phoneNumber,
                websiteUri: profile.websiteUri || profile.website,
                categories: profile.categories || {},
                storefrontAddress: profile.storefrontAddress || profile.address || {},
                regularHours: profile.regularHours || {},
                latlng: profile.latlng || profile.location?.latlng,
                businessStatus: profile.openInfo?.status || profile.businessStatus,
                openInfo: profile.openInfo || {}
            };

            // Fetch additional data if needed
            try {
                const accessToken = await GMBService.getGoogleToken();
                const accountId = profile.accountId;
                const locationId = profile.locationId;

                // CRITICAL: Fetch VoiceOfMerchant state for accurate verification check
                try {
                    const vom = await GMBService.getVoiceOfMerchantState(locationId, accessToken);
                    gmbDataPayload.voiceOfMerchant = vom;
                    gmbDataPayload.verificationStatus = vom?.simplifiedStatus || 'unknown';
                    gmbDataPayload.isVerified = vom?.simplifiedStatus === 'verified';
                    gmbDataPayload.hasVoiceOfMerchant = vom?.raw?.hasVoiceOfMerchant === true;
                    gmbDataPayload.hasBusinessAuthority = vom?.raw?.hasBusinessAuthority === true;
                    logger.log('[TaskManagementPage] VOM state:', vom?.simplifiedStatus, vom?.raw);
                } catch (vomErr) {
                    logger.warn('[TaskManagementPage] VOM fetch failed, using fallback:', vomErr?.message);
                    // Fallback to metadata.verified
                    gmbDataPayload.verificationStatus = profile.metadata?.verified ? 'verified' : 'not_verified';
                    gmbDataPayload.isVerified = profile.metadata?.verified === true || profile.verified === true;
                }

                // Fetch media (photos)
                if (accessToken && accountId && locationId) {
                    try {
                        const media = await GMBService.getMedia(accessToken, accountId, locationId);
                        gmbDataPayload.media = media || [];
                        logger.log('[TaskManagementPage] Media loaded:', media?.length || 0);
                    } catch (mediaErr) {
                        logger.warn('[TaskManagementPage] Media fetch failed:', mediaErr);
                        gmbDataPayload.media = [];
                    }
                }

                // Fetch reviews count
                try {
                    const reviews = await GMBService.getReviews(accountId, locationId);
                    gmbDataPayload.reviews = reviews?.reviews || [];
                    gmbDataPayload.reviewCount = reviews?.totalReviewCount || gmbDataPayload.reviews.length;
                    logger.log('[TaskManagementPage] Reviews loaded:', gmbDataPayload.reviewCount);
                } catch (revErr) {
                    logger.warn('[TaskManagementPage] Reviews fetch failed:', revErr);
                    gmbDataPayload.reviews = [];
                    gmbDataPayload.reviewCount = 0;
                }

            } catch (tokenErr) {
                logger.warn('[TaskManagementPage] Token error, using basic profile data:', tokenErr);
            }

            logger.log('[TaskManagementPage] GMB data compiled:', {
                verified: gmbDataPayload.metadata?.verified,
                hasPhone: !!gmbDataPayload.phoneNumber,
                hasWebsite: !!gmbDataPayload.websiteUri,
                photoCount: gmbDataPayload.media?.length || 0
            });

            // Add competitor insights for category suggestion task
            try {
                const profileId = profile.locationId || profile.id;
                const competitorInsights = getCompetitorInsights(profileId);
                if (competitorInsights) {
                    gmbDataPayload.competitorInsights = competitorInsights;
                    logger.log('[TaskManagementPage] Competitor insights loaded:', competitorInsights.suggestedPrimaryCategory);
                }
            } catch (insightsErr) {
                logger.warn('[TaskManagementPage] Failed to load competitor insights:', insightsErr);
            }

            // Fetch competitor analysis from database for review task calculation
            try {
                // Debug: Log all possible userId sources
                console.log('=== USER ID DEBUG ===');
                console.log('profile.user_id:', profile.user_id);
                console.log('profile.userId:', profile.userId);
                console.log('profile.owner_id:', profile.owner_id);
                console.log('sessionStorage adminViewingUserId:', sessionStorage.getItem('adminViewingUserId'));
                console.log('profile keys:', Object.keys(profile));
                
                // Get userId - Priority: profile.user_id > adminViewingUserId > tempKey data > stored user data
                let userId = null;
                
                // First try to get user_id from the profile itself (most accurate for this location)
                if (profile.user_id || profile.userId || profile.owner_id) {
                    userId = profile.user_id || profile.userId || profile.owner_id;
                    console.log('Using userId from profile:', userId);
                }
                
                // Check if admin is viewing client's data
                if (!userId) {
                    const adminViewingUserId = sessionStorage.getItem('adminViewingUserId');
                    if (adminViewingUserId) {
                        userId = adminViewingUserId;
                        console.log('Using adminViewingUserId:', userId);
                    }
                }
                
                // Check tempKey data (admin access via URL)
                if (!userId) {
                    const tempKey = sessionStorage.getItem('adminViewingGmbTempKey') || 
                                    new URLSearchParams(window.location.search).get('tempKey');
                    if (tempKey) {
                        const tempData = localStorage.getItem(tempKey);
                        if (tempData) {
                            try {
                                const parsed = JSON.parse(tempData);
                                userId = parsed?.targetUserId;
                                console.log('Using targetUserId from tempKey:', userId);
                            } catch (e) { }
                        }
                    }
                }
                
                // Check URL param
                if (!userId) {
                    const urlUserId = new URLSearchParams(window.location.search).get('userId');
                    if (urlUserId) {
                        userId = urlUserId;
                        console.log('Using userId from URL:', userId);
                    }
                }
                
                // Fallback: Get userId from stored JSON objects (logged-in user)
                if (!userId) {
                    // Check userData first
                    const userDataRaw = localStorage.getItem('userData');
                    if (userDataRaw) {
                        try {
                            const userData = JSON.parse(userDataRaw);
                            userId = userData?.id || userData?.user_id;
                        } catch (e) { }
                    }
                    
                    // Fallback to auth
                    if (!userId) {
                        const authRaw = localStorage.getItem('auth');
                        if (authRaw) {
                            try {
                                const auth = JSON.parse(authRaw);
                                userId = auth?.id || auth?.user?.id || auth?.data?.id;
                            } catch (e) { }
                        }
                    }
                    
                    // Fallback to user
                    if (!userId) {
                        const userRaw = localStorage.getItem('user');
                        if (userRaw) {
                            try {
                                const user = JSON.parse(userRaw);
                                userId = user?.id;
                            } catch (e) { }
                        }
                    }
                }
                
                const locationId = profile.locationId || profile.id;
                
                console.log('=== COMPETITOR FETCH DEBUG ===');
                console.log('userId:', userId);
                console.log('locationId:', locationId);
                
                if (userId && locationId) {
                    console.log('Calling CompetitorAnalysisService.getLatestByLocation...');
                    const response = await CompetitorAnalysisService.getLatestByLocation(userId, locationId);
                    console.log('API Response:', response);
                    
                    // API returns { success: true, data: { competitors_data: {...} } }
                    const competitorAnalysis = response?.data || response;
                    console.log('competitorAnalysis:', competitorAnalysis);
                    console.log('competitors_data exists:', !!competitorAnalysis?.competitors_data);
                    
                    if (competitorAnalysis && competitorAnalysis.competitors_data) {
                        gmbDataPayload.competitorAnalysis = competitorAnalysis;
                        
                        // Parse competitors_data if it's a string
                        let competitorsData = competitorAnalysis.competitors_data;
                        if (typeof competitorsData === 'string') {
                            try {
                                competitorsData = JSON.parse(competitorsData);
                            } catch (e) {
                                logger.warn('[TaskManagementPage] Failed to parse competitors_data JSON');
                            }
                        }
                        
                        const competitors = competitorsData?.competitors || competitorsData || [];
                        console.log('=== COMPETITOR DATA LOADED ===');
                        console.log('competitorsData:', competitorsData);
                        console.log('competitors array:', competitors);
                        logger.log('[TaskManagementPage] Competitor analysis loaded:', {
                            keyword: competitorAnalysis.keyword,
                            competitorCount: Array.isArray(competitors) ? competitors.length : 0,
                            myRank: competitorsData?.myRank,
                            hasCompetitors: Array.isArray(competitors) && competitors.length > 0
                        });
                    } else {
                        console.log('=== NO COMPETITOR DATA ===');
                        logger.log('[TaskManagementPage] No competitor analysis found for this location. Response:', response);
                    }
                } else {
                    console.log('=== MISSING userId or locationId ===');
                }
            } catch (compErr) {
                // Don't fail the whole page if competitor analysis fails
                console.log('=== COMPETITOR FETCH ERROR ===', compErr);
                logger.warn('[TaskManagementPage] Failed to fetch competitor analysis:', compErr?.message);
            }

            setGmbData(gmbDataPayload);
            setSelectedProfile(profile);

        } catch (err) {
            logger.error('[TaskManagementPage] Error fetching GMB data:', err);
            setError('Failed to load profile data. Please try again.');
        } finally {
            setDataLoading(false);
        }
    }, []);

    // ============================================
    // Effects
    // ============================================

    useEffect(() => {
        fetchProfiles();
    }, [fetchProfiles]);

    useEffect(() => {
        if (selectedProfileId && profiles.length > 0) {
            const profile = profiles.find(p => p.id === selectedProfileId || p.locationId === selectedProfileId);
            if (profile) {
                fetchGMBData(profile);
            }
        }
    }, [selectedProfileId, profiles, fetchGMBData]);

    // Generate system tasks and send WhatsApp notifications when GMB data is loaded
    useEffect(() => {
        const generateAndNotify = async () => {
            if (!selectedProfile || !gmbData) return;

            const token = localStorage.getItem('token');
            if (!token) {
                logger.log('[TaskManagementPage] No auth token, skipping task generation');
                return;
            }

            const profileId = selectedProfile.locationId || selectedProfile.id;
            logger.log('[TaskManagementPage] Generating system tasks for profile:', profileId);

            try {
                // Step 1: Generate system tasks based on GMB data (saves to DB + sends WhatsApp)
                const generateResponse = await fetch(`${API_BASE_URL}/customer/task/generate-system-tasks`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ profileId, gmbData })
                });

                const generateResult = await generateResponse.json();

                if (generateResult.success) {
                    logger.log('[TaskManagementPage] ✅ System tasks generated:', generateResult.data?.generatedCount || 0);
                } else {
                    logger.warn('[TaskManagementPage] Task generation failed:', generateResult.message);
                }
            } catch (err) {
                logger.warn('[TaskManagementPage] Failed to generate system tasks:', err.message);
            }

            // Step 2: Send pending notifications for any tasks not yet notified
            try {
                logger.log('[TaskManagementPage] Checking for pending WhatsApp notifications for profile:', profileId);

                const response = await fetch(`${API_BASE_URL}/customer/task/send-pending-notifications`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ profileId })
                });

                const result = await response.json();

                if (result.success && result.data?.notificationsSent > 0) {
                    logger.log('[TaskManagementPage] ✅ WhatsApp notifications sent:', result.data.notificationsSent);
                } else {
                    logger.log('[TaskManagementPage] No pending notifications or already sent:', result.message);
                }
            } catch (err) {
                // Don't block page load if notification fails
                logger.warn('[TaskManagementPage] Failed to send pending notifications:', err.message);
            }
        };

        generateAndNotify();
    }, [selectedProfile, gmbData]);

    // ============================================
    // Handlers
    // ============================================

    const handleProfileChange = (event) => {
        setSelectedProfileId(event.target.value);
    };

    const handleRefresh = () => {
        if (selectedProfile) {
            fetchGMBData(selectedProfile);
        }
    };

    /**
     * ✅ FIX: Handle photo uploads locally without full API refresh
     * This prevents page refresh and updates the counter immediately
     */
    const handlePhotosUploaded = useCallback((uploadedPhotos) => {
        if (!uploadedPhotos || uploadedPhotos.length === 0) return;

        logger.log('[TaskManagementPage] 📸 Adding uploaded photos to local state:', uploadedPhotos.length);

        setGmbData(prevData => {
            if (!prevData) return prevData;

            // Add newly uploaded photos to media array
            const newMedia = uploadedPhotos.map(photo => ({
                name: photo.mediaItemId || photo.name,
                googleUrl: photo.googleUrl || photo.url || photo.sourceUrl,
                sourceUrl: photo.sourceUrl || photo.url,
                thumbnailUrl: photo.thumbnailUrl,
                mediaFormat: 'PHOTO',
                createTime: photo.uploadedAt || new Date().toISOString(),
                category: photo.category || 'ADDITIONAL'
            }));

            const updatedMedia = [
                ...(prevData.media || []),
                ...newMedia
            ];

            logger.log('[TaskManagementPage] ✅ Updated media count:', {
                previous: prevData.media?.length || 0,
                added: newMedia.length,
                total: updatedMedia.length
            });

            return {
                ...prevData,
                media: updatedMedia
            };
        });
    }, []);

    // ============================================
    // Render
    // ============================================

    if (loading) {
        return (
            <PageContainer>
                <LoadingContainer>
                    <CircularProgress size={48} sx={{ color: '#0b91d6', mb: 2 }} />
                    <Typography sx={{ color: '#64748b' }}>
                        Loading your business profiles...
                    </Typography>
                </LoadingContainer>
            </PageContainer>
        );
    }

    if (error && profiles.length === 0) {
        return (
            <PageContainer>
                <Box sx={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center', py: 8 }}>
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                        {error}
                    </Alert>
                    <Button
                        variant="contained"
                        onClick={fetchProfiles}
                        startIcon={<RefreshIcon />}
                        sx={{ borderRadius: '10px' }}
                    >
                        Try Again
                    </Button>
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            {/* Profile Selector */}
            <ProfileSelectorCard elevation={0}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1.5, sm: 2 },
                    width: { xs: '100%', sm: 'auto' }
                }}>
                    <BusinessIcon sx={{ color: '#0b91d6', fontSize: { xs: 24, sm: 28 } }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{
                            color: '#64748b',
                            fontSize: { xs: '11px', sm: '12px' },
                            mb: 0.5
                        }}>
                            SELECT PROFILE
                        </Typography>
                        <ProfileSelect size="small">
                            <Select
                                value={selectedProfileId}
                                onChange={handleProfileChange}
                                displayEmpty
                                sx={{ fontSize: { xs: '14px', sm: '16px' } }}
                            >
                                {profiles.map(profile => (
                                    <MenuItem key={profile.id} value={profile.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                                                {profile.title}
                                            </Typography>
                                            {profile.verified && (
                                                <Chip
                                                    label="Verified"
                                                    size="small"
                                                    sx={{
                                                        height: '20px',
                                                        fontSize: '10px',
                                                        bgcolor: '#dcfce7',
                                                        color: '#166534'
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </ProfileSelect>
                    </Box>
                </Box>

                <Button
                    variant="outlined"
                    onClick={handleRefresh}
                    startIcon={<RefreshIcon />}
                    disabled={dataLoading}
                    sx={{
                        borderRadius: '10px',
                        width: { xs: '100%', sm: 'auto' },
                        minHeight: { xs: '44px', sm: '40px' },
                        fontSize: { xs: '14px', sm: '13px' }
                    }}
                >
                    Refresh
                </Button>
            </ProfileSelectorCard>

            {/* Error Alert */}
            {error && (
                <Box sx={{ maxWidth: '900px', margin: '0 auto', mb: 2 }}>
                    <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                        {error}
                    </Alert>
                </Box>
            )}

            {/* Task Management Component */}
            <TaskManagement
                gmbData={gmbData}
                selectedProfile={selectedProfile}
                onRefresh={handleRefresh}
                onPhotosUploaded={handlePhotosUploaded}
                isLoading={dataLoading}
            />
        </PageContainer>
    );
}

export default TaskManagementPage;
